import Emitter from './emitter';
import {
  successReciveMessageReturnString,
  JSBridgeCallbackName,
  subscribeMessageHeader,
  responseHeader
} from './const';
import {
  _isRequest,
  _getRealMethodFromRequest,
  _isSubscribeMessage,
  _isRequestRespones,
  _generateMessageData,
  _generateSubscribeMethod,
  _validateTarget
} from './utils';
import logCollector from '../LogCollector';

function _disPatchRespones(responese) {
  const responeseObj = JSON.parse(responese);
  const { method, requestId, params } = responeseObj;

  // 获取真正的方法名：real_method: changeSharerStatus
  const realMethod = _getRealMethodFromRequest(method);
  // 封装为自己监听的请求方法名：total_method: sub.changeSharerStatus
  const total_method = _generateSubscribeMethod(realMethod);
  // 获取请求的响应并且发送给客户端
  const getResponseAndSend = async () => {
    // response: {code, data, message}
    const response = await this.emitter.emitWithOneResponse(total_method, params);
    const result = {
      method: `${responseHeader}${realMethod}`,
      requestId,
      code: response.code === 200 ? '000000' : '999999',
      message: response.message
    };

    this._postMessage(JSON.stringify(result));
  };
  // 检查事件是否已经注册回调，如果没有则不处理
  if (!this.emitter.has(method) && !this.emitter.has(requestId) && !this.emitter.has(total_method)) {
    return;
  }
  // 客户端发送过来的请求
  if (_isRequest(method)) {
    getResponseAndSend();
  }
  // 异步请求的回复
  else if (_isRequestRespones(method)) {
    this.emitter.emit(requestId, responeseObj);
  }
  // 订阅的消息
  else if (_isSubscribeMessage(method)) {
    this.emitter.emit(method, responeseObj);
  }
  // 未知的协议头
  else {
    throw new Error(`unkown method ${method}: ${responese}`);
  }
  // return一个字符串 告知已接收成功
  return successReciveMessageReturnString;
}

class JSBridgeCore {
  constructor() {
    this.initialize = false;
    this.emitter = new Emitter();
    this.target = null;
    this.init = this.init.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this._postMessage = this._postMessage.bind(this);
    this._reciveMessage = this._reciveMessage.bind(this);
  }
  /**
   * 接收数据
   * @param {string} responese
   */
  _reciveMessage(responese) {
    try {
      logCollector.info('[jsbridge-responese]', responese);

      // 未初始化前不处理任何消息
      if (!this.initialize) return;

      return _disPatchRespones.call(this, responese);
    } catch (error) {
      throw new Error(error);
    }
  }
  /**
   * 发送数据
   * @param {string} request 发送的请求
   */
  _postMessage(request) {
    try {
      logCollector.info('[jsbridge-request]', request);

      return this.target.requestFromJS(request);
    } catch (error) {
      throw new Error(error);
    }
  }

  /**
   * 初始化，挂载native注入的对象
   * @param {object} target native注入的对象
   */
  init(target) {
    // 校验传入的目标对象
    if (_validateTarget(target)) {
      this.target = target;
      window[JSBridgeCallbackName] = this._reciveMessage;
      this.initialize = true;
      return this;
    }
  }

  /**
   * 异步请求
   * @param {string} method 请求方法
   * @param {object} data 传递的数据
   * @param {function} callback 回调函数
   */
  request(method, data, callback) {
    try {
      if (!this.initialize) {
        throw new Error('JSBridge not initialize');
      }
      const { request, requestId } = _generateMessageData(true, method, data);
      if (callback) {
        this.emitter.once(requestId, callback);
      }
      this._postMessage(request);
      return requestId;
    } catch (error) {
      throw new Error(error);
    }
  }

  cancel(requestId, callback) {
    this.emitter.off(requestId, callback);
  }

  /**
   * 同步请求
   * @param {string} method 请求方法
   * @param {object} data 传递的数据
   */
  syncRequest(method, data) {
    try {
      if (!this.initialize) {
        throw new Error('JSBridge not initialize');
      }
      const { request } = _generateMessageData(false, method, data);
      const res = this._postMessage(request);

      logCollector.info('[jsbridge-sync-responese]', res);

      return res;
    } catch (error) {
      throw new Error(error);
    }
  }
  /**
   * 消息推送
   * @param {string} method 请求方法
   * @param {object} data 传递的数据
   */
  sendMessage(method, data) {
    try {
      if (!this.initialize) {
        throw new Error('JSBridge not initialize');
      }
      const { request } = _generateMessageData(true, method, data);
      this._postMessage(request);
    } catch (error) {
      throw new Error(error);
    }
  }

  /**
   * 消息发布,向安卓发布事件
   * 与sendMessage的区别在于向安卓发送消息的 method头不同，方便安卓对其进行不同的处理
   * 与subscribe的区别在于subscribe只监听事件，而没有向安卓发布事件
   * @param {string} method 请求方法
   * @param {object} data 传递的数据
   */
  publishMessage(method, data) {
    try {
      if (!this.initialize) {
        throw new Error('JSBridge not initialize');
      }
      const { request } = _generateMessageData(true, method, data, subscribeMessageHeader, 'data');
      this._postMessage(request);
    } catch (error) {
      throw new Error(error);
    }
  }
  /**
   * 消息订阅
   * @param {string} method 订阅的事件
   * @param {function} callback 回调的函数
   */
  subscribe(method, callback) {
    const total_method = _generateSubscribeMethod(method);
    this.emitter.on(total_method, callback);
  }
  /**
   * 消息订阅取消
   * @param {string} method 订阅的事件
   * @param {function} callback 回调的函数
   */
  unsubscribe(method, callback) {
    const total_method = _generateSubscribeMethod(method);
    this.emitter.off(total_method, callback);
  }

  // 模拟处理客户端发来请求的情况
  async emit(method, params) {
    const total_method = _generateSubscribeMethod(method);
    const response = await this.emitter.emitWithOneResponse(total_method, params);
    console.log(response);
    const result = {
      method: `${responseHeader}${method}`,
      ...response
    };
    console.log(result);
  }
}

const jsBridgeCore = new JSBridgeCore();

export default jsBridgeCore;
